/**
 * Балансировщик ликвидности Фабрики платежей: хватает ли денег расчётных счетов на
 * утверждённые и ещё не оплаченные заявки, и доли шкалы. Уже оплаченное по выписке на
 * шкалу не попадает: эти деньги ушли со счёта раньше, чем снят текущий остаток.
 * Модуль без импортов — проверяется node:test (`liquidity.test.mjs`).
 */

export interface Liquidity {
  /** Сколько процентов остатка к списанию покрывают деньги на счетах. */
  coveragePct: number;
  /** Деньги на счетах после всех предстоящих списаний; меньше нуля — не хватает. */
  projectedCash: number;
  /** Сколько не хватает на предстоящие списания; 0 — хватает. */
  deficit: number;
  /** Доля шкалы под предстоящие списания, % её ширины. */
  debitPct: number;
  /** Доля шкалы под свободный остаток, %. Вместе с `debitPct` — не больше 100. */
  restPct: number;
}

/**
 * Расчёт по подтверждённым данным: `cash` — доступные деньги расчётных счетов с пульта,
 * `remainingToDebit` — не оплаченный остаток утверждённых заявок. Нет любого из них (не
 * загрузился пульт или список заявок) — расчёта нет: ноль обязательств, которых просто не
 * видно, дал бы ложные 100 %.
 */
export function computeLiquidity(
  cash: number | null,
  remainingToDebit: number | null,
): Liquidity | null {
  if (cash === null || remainingToDebit === null) return null;
  const available = Math.max(0, cash);
  const debit = Math.max(0, remainingToDebit);
  const projectedCash = cash - debit;
  const debitPct = available > 0 ? (Math.min(debit, available) / available) * 100 : 0;
  return {
    coveragePct: debit > 0 ? (available / debit) * 100 : 100,
    projectedCash,
    deficit: Math.max(0, -projectedCash),
    debitPct,
    restPct: available > 0 ? 100 - debitPct : 0,
  };
}
